/**
 * Config Presets Store Core (Factory)
 *
 * Shared logic for the config presets store.
 * Both web and mobile create their own Zustand store by calling
 * `createPresetsSlice(deps)` and wrapping it with platform-specific
 * persistence and config subscription.
 *
 * Every config IS a preset. There's always a "Default" preset, plus user-created ones.
 * Synced via PUT /api/auth/config-presets (custom presets only -- Default is derived).
 */

import type { StateCreator } from 'zustand';
import type { AnalysisConfig } from '../analysisConfig';
import { ApiError } from '../api/httpClient';
import type { ConfigPreset } from '../configPreset';
import { DEFAULT_PRESET_ID, MAX_CUSTOM_PRESETS, MAX_PRESET_NAME_LENGTH } from '../configPreset';
import type { LeagueKey } from '../league';

// ---------------------------------------------------------------------------
// Dependencies injected by each platform
// ---------------------------------------------------------------------------

export interface PresetsStoreDeps {
  /** Platform-specific fetch wrapper */
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** Read the current analysis config from the platform's config store */
  getConfig: () => AnalysisConfig;
  /** Write a config into the platform's config store */
  setConfig: (config: AnalysisConfig) => void;
  /**
   * Optional deep-merge function for applying a preset's config on top of defaults.
   * Web provides `deepMergeConfig`; mobile uses direct assignment (no merge needed).
   * When omitted, the preset's config is passed directly to `setConfig`.
   */
  mergeConfig?: (base: AnalysisConfig, override: Partial<AnalysisConfig>) => AnalysisConfig;
  /** Generate a unique ID for new presets (crypto.randomUUID on web, fallback on mobile) */
  generateId: () => string;
  /**
   * Optional callback after presets or activePresetId changes, for platform-specific
   * persistence (e.g., localStorage on web). Zustand persist handles this on mobile.
   */
  onPersist?: (presets: ConfigPreset[], activePresetId: string) => void;
}

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------

export interface PresetsState {
  presets: ConfigPreset[];
  activePresetId: string;

  /** Load custom presets from user's DB record (login/refresh) */
  loadFromUser: (customPresets: ConfigPreset[] | null) => void;
  /** Sign-out: back to Default only, built from the current config. No server request. */
  resetLocal: () => void;
  /** Switch to a different preset -- loads its config into analysisConfigStore */
  switchPreset: (presetId: string) => void;
  /** Create a new preset from the current config */
  createPreset: (
    name: string,
    authFetch: (url: string, init?: RequestInit) => Promise<Response>,
    league?: LeagueKey,
  ) => Promise<{ success: boolean; error?: string }>;
  /** Delete a custom preset (can't delete Default) */
  deletePreset: (
    presetId: string,
    authFetch: (url: string, init?: RequestInit) => Promise<Response>,
  ) => Promise<void>;
  /** Rename a custom preset (can't rename Default) */
  renamePreset: (
    presetId: string,
    newName: string,
    authFetch: (url: string, init?: RequestInit) => Promise<Response>,
  ) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Create a Default preset with a given config (or empty if none provided) */
export function makeDefaultPreset(config?: AnalysisConfig): ConfigPreset {
  const now = new Date().toISOString();
  return {
    id: DEFAULT_PRESET_ID,
    name: 'Default',
    config: config ? JSON.parse(JSON.stringify(config)) : ({} as AnalysisConfig),
    createdAt: now,
    updatedAt: now,
  };
}

/** Get only custom presets (exclude Default) for server sync */
export function getCustomPresets(presets: ConfigPreset[]): ConfigPreset[] {
  return presets.filter((p) => p.id !== DEFAULT_PRESET_ID);
}

/** Sync custom presets to the server */
export async function syncPresetsToServer(
  customPresets: ConfigPreset[],
  authFetch: (url: string, init?: RequestInit) => Promise<Response>,
): Promise<void> {
  const res = await authFetch('/api/auth/config-presets', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ configPresets: customPresets }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(data.error || 'Failed to sync presets', res.status, undefined, data);
  }
}

// ---------------------------------------------------------------------------
// Switching guard — shared mutable flag
// ---------------------------------------------------------------------------

/** Module-level flag to prevent config->preset subscription loops during switches */
let _isSwitching = false;

/** Check if a preset switch is in progress (used by platform config subscriptions) */
export function isSwitchingPreset(): boolean {
  return _isSwitching;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createPresetsSlice(
  deps: PresetsStoreDeps,
  initialPresets: ConfigPreset[] = [makeDefaultPreset()],
  initialActiveId: string = DEFAULT_PRESET_ID,
): StateCreator<PresetsState> {
  /** Apply a preset's config via deps, with optional merge */
  function applyPresetConfig(presetConfig: AnalysisConfig): void {
    if (deps.mergeConfig) {
      // Web path: deep-merge the preset config on top of defaults
      // The defaults must be provided by the caller via mergeConfig's first arg binding
      const merged = deps.mergeConfig(
        {} as AnalysisConfig,
        presetConfig as Partial<AnalysisConfig>,
      );
      deps.setConfig(merged);
    } else {
      deps.setConfig(presetConfig);
    }
  }

  return (set, get) => ({
    presets: initialPresets,
    activePresetId: initialActiveId,

    loadFromUser: (customPresets) => {
      const customs = customPresets ?? [];
      const currentConfig = deps.getConfig();
      const defaultPreset = makeDefaultPreset(currentConfig);
      const allPresets = [defaultPreset, ...customs];

      // Validate activePresetId — check current state first, fall back to default
      const { activePresetId: currentActiveId } = get();
      const activeId = allPresets.some((p) => p.id === currentActiveId)
        ? currentActiveId
        : DEFAULT_PRESET_ID;

      set({ presets: allPresets, activePresetId: activeId });
      deps.onPersist?.(allPresets, activeId);

      // If the active preset isn't Default, apply its config
      if (activeId !== DEFAULT_PRESET_ID) {
        const activePreset = customs.find((p) => p.id === activeId);
        if (activePreset) {
          _isSwitching = true;
          applyPresetConfig(activePreset.config);
          _isSwitching = false;
        }
      }
    },

    resetLocal: () => {
      set({ presets: [makeDefaultPreset(deps.getConfig())], activePresetId: DEFAULT_PRESET_ID });
    },

    switchPreset: (presetId) => {
      const { presets } = get();
      const preset = presets.find((p) => p.id === presetId);
      if (!preset) return;

      _isSwitching = true;
      applyPresetConfig(preset.config);
      set({ activePresetId: presetId });
      deps.onPersist?.(presets, presetId);
      _isSwitching = false;
    },

    createPreset: async (name, authFetch, league) => {
      const { presets } = get();
      const trimmedName = name.trim();
      const customCount = getCustomPresets(presets).length;

      if (trimmedName.length === 0) {
        return { success: false, error: 'Preset name is required' };
      }
      if (trimmedName.length > MAX_PRESET_NAME_LENGTH) {
        return {
          success: false,
          error: `Name must be ${MAX_PRESET_NAME_LENGTH} characters or less`,
        };
      }
      if (customCount >= MAX_CUSTOM_PRESETS) {
        return { success: false, error: `Maximum ${MAX_CUSTOM_PRESETS} custom presets allowed` };
      }

      const currentConfig = deps.getConfig();
      const now = new Date().toISOString();
      const newPreset: ConfigPreset = {
        id: deps.generateId(),
        name: trimmedName,
        config: JSON.parse(JSON.stringify(currentConfig)),
        league,
        createdAt: now,
        updatedAt: now,
      };

      const updated = [...presets, newPreset];
      set({ presets: updated, activePresetId: newPreset.id });
      deps.onPersist?.(updated, newPreset.id);

      try {
        await syncPresetsToServer(getCustomPresets(updated), authFetch);
        return { success: true };
      } catch (err) {
        // Rollback
        set({ presets, activePresetId: get().activePresetId });
        deps.onPersist?.(presets, get().activePresetId);
        return { success: false, error: ApiError.getUserMessage(err, 'Failed to save preset') };
      }
    },

    deletePreset: async (presetId, authFetch) => {
      if (presetId === DEFAULT_PRESET_ID) return; // Can't delete Default

      const { presets, activePresetId } = get();
      const updated = presets.filter((p) => p.id !== presetId);
      const newActiveId = activePresetId === presetId ? DEFAULT_PRESET_ID : activePresetId;

      set({ presets: updated, activePresetId: newActiveId });
      deps.onPersist?.(updated, newActiveId);

      // If we deleted the active preset, switch to Default's config
      if (activePresetId === presetId) {
        const defaultPreset = updated.find((p) => p.id === DEFAULT_PRESET_ID);
        if (defaultPreset) {
          _isSwitching = true;
          applyPresetConfig(defaultPreset.config);
          _isSwitching = false;
        }
      }

      try {
        await syncPresetsToServer(getCustomPresets(updated), authFetch);
      } catch {
        set({ presets, activePresetId });
        deps.onPersist?.(presets, activePresetId);
      }
    },

    renamePreset: async (presetId, newName, authFetch) => {
      if (presetId === DEFAULT_PRESET_ID) return; // Can't rename Default

      const { presets } = get();
      const trimmedName = newName.trim();
      if (trimmedName.length === 0 || trimmedName.length > MAX_PRESET_NAME_LENGTH) return;

      const updated = presets.map((p) =>
        p.id === presetId ? { ...p, name: trimmedName, updatedAt: new Date().toISOString() } : p,
      );
      set({ presets: updated });
      deps.onPersist?.(updated, get().activePresetId);

      try {
        await syncPresetsToServer(getCustomPresets(updated), authFetch);
      } catch {
        set({ presets });
        deps.onPersist?.(presets, get().activePresetId);
      }
    },
  });
}
